import React from "react";
import { StyleSheet, Text, View } from "react-native";
import { MultiSelect } from "react-native-element-dropdown";
import { Controller, ControllerProps, FieldValues } from "react-hook-form";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
} from "ui/gluestack/form-control";
import { AlertCircleIcon } from "ui/gluestack/icon";

export interface MultiSelectDropdownProps<
  TFieldValues extends FieldValues = FieldValues,
> {
  name: ControllerProps<TFieldValues>["name"];
  label?: string;
  placeholder?: string;
  helperText?: string;
  invalidMessage?: string;
  options: { value: number | string; label: string }[];
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  control: ControllerProps<TFieldValues>["control"];
  rules?: ControllerProps<TFieldValues>["rules"];
  onValueChange?: (value?: (string | number)[]) => void;
  onSearchChange?: (searchText: string) => void;
}

export function FormControlledMultiSelectDropdown<
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  label,
  placeholder,
  helperText,
  invalidMessage,
  options,
  isDisabled,
  isReadOnly,
  isRequired,
  isInvalid,
  control,
  rules,
  onValueChange,
  onSearchChange,
}: MultiSelectDropdownProps<TFieldValues>) {
  if (!control) return null;

  const HINT_VALUE = "__info__";

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ fieldState: { error }, field: { value = [], onChange } }) => {
        const isFieldInvalid = isInvalid || !!error;
        const safeValue: (string | number)[] = Array.isArray(value)
          ? value
          : [];

        const enhancedOptions = [
          {
            label: "Select 1 or more from the list",
            value: HINT_VALUE,
            disabled: true,
          },
          ...options,
        ];

        const handleChange = (selectedValues: (string | number)[]) => {
          const filtered = (selectedValues || []).filter(
            (v) => v !== HINT_VALUE,
          );
          onChange(filtered.length ? filtered : null);

          if (onValueChange) {
            onValueChange(filtered);
          }
        };

        return (
          <FormControl
            isInvalid={isFieldInvalid}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            isRequired={isRequired}
          >
            {label ? (
              <Text className="test-sm font-medium mb-2 text-gray-600">
                {label}
                {!isRequired ? " (Optional)" : ""}
              </Text>
            ) : null}

            <View className={"my-1"}>
              <MultiSelect
                style={[
                  styles.dropdown,
                  isFieldInvalid && styles.invalidDropdown,
                  isDisabled && styles.disabledDropdown,
                ]}
                placeholderStyle={styles.placeholderStyle}
                selectedTextStyle={styles.selectedTextStyle}
                selectedStyle={styles.selectedStyle}
                containerStyle={styles.containerStyle}
                inputSearchStyle={styles.inputSearchStyle}
                activeColor="#EBEFFF"
                iconStyle={styles.iconStyle}
                search
                disable={isDisabled}
                data={enhancedOptions}
                labelField="label"
                valueField="value"
                placeholder={placeholder || "Select item"}
                searchPlaceholder="Search..."
                value={safeValue}
                onChange={handleChange}
                onChangeText={(text) => onSearchChange && onSearchChange(text)}
                renderItem={(item) => {
                  const isInfo = item.value === HINT_VALUE;

                  return (
                    <View
                      pointerEvents={isInfo ? "none" : "auto"}
                      style={[
                        isInfo ? styles.infoItem : styles.item,
                        item.disabled && !isInfo && styles.disabledItem,
                      ]}
                    >
                      <Text
                        style={[
                          isInfo ? styles.infoText : styles.itemText,
                          item.disabled && !isInfo && styles.disabledText,
                        ]}
                      >
                        {item.label}
                      </Text>
                    </View>
                  );
                }}
              />
            </View>

            {helperText && (
              <FormControlHelper>
                <FormControlHelperText>{helperText}</FormControlHelperText>
              </FormControlHelper>
            )}

            {(invalidMessage || error?.message) && (
              <FormControlError>
                <FormControlErrorIcon
                  as={AlertCircleIcon}
                  className="text-error-500"
                  size="sm"
                />
                <FormControlErrorText className="text-error-500 text-sm">
                  {error?.message || invalidMessage}
                </FormControlErrorText>
              </FormControlError>
            )}
          </FormControl>
        );
      }}
    />
  );
}

const styles = StyleSheet.create({
  disabledDropdown: { opacity: 0.5 },
  dropdown: {
    backgroundColor: "transparent",
    borderColor: "#D1D5DB",
    borderRadius: 4,
    borderWidth: 1,
    height: 40,
    paddingLeft: 10,
    paddingRight: 10,
  },
  containerStyle: {
    elevation: 5,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
  },
  iconStyle: { height: 20, width: 20 },
  inputSearchStyle: { color: "#000", fontSize: 16, height: 40, padding: 0 },
  invalidDropdown: { borderColor: "#B91C1C", borderWidth: 2 },
  placeholderStyle: { color: "#667085", fontSize: 14 },
  selectedStyle: {
    backgroundColor: "#EBEFFF",
    borderColor: "transparent",
    borderRadius: 4,
  },
  selectedTextStyle: { color: "#475467", fontSize: 14 },

  // item styles
  item: { paddingHorizontal: 17, paddingVertical: 17 },
  itemText: { color: "#000", fontSize: 16 },

  // disabled item
  disabledItem: { opacity: 0.5 },
  disabledText: { color: "#9CA3AF" },

  // hint / info row
  infoItem: {
    backgroundColor: "#F9FAFB",
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  infoText: { color: "#374151", fontSize: 12, fontStyle: "italic" }, // darker gray
});
